import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';

import { User } from '../types/user';

interface TopBarProps {
  user?: User | null;  // Made optional with ? to match MainLayout
}

// ── HCI brand tokens (from the logo) ──────────────────────────────────────────
const C = {
  navy:     '#14267A',
  navyDeep: '#0B1650',
  gold:     '#F5B326',
  tint:     '#E8EBF8',
  tintSoft: '#F4F5FA',
  line:     '#E2E5F2',
  lineStrong: '#DADFF0',
  text:     '#12183A',
  textSoft: '#5A6080',
};

const NOTIFICATIONS = [
  { icon: 'pi-user',     text: 'New student registration',    time: '5 min ago',   bg: '#E8EBF8', fg: '#14267A' },
  { icon: 'pi-calendar', text: 'Exam scheduled for tomorrow', time: '1 hour ago',  bg: '#FFE9BF', fg: '#8A4B00' },
  { icon: 'pi-bell',     text: 'Fee payment reminder',        time: '2 hours ago', bg: '#E3F6C4', fg: '#2C5A12' },
];

const iconBtn = (active = false): React.CSSProperties => ({
  width: '36px',
  height: '36px',
  background: active ? C.tint : 'white',
  border: `1px solid ${C.lineStrong}`,
  borderRadius: '11px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  transition: 'all 0.2s ease',
});

const TopBar: React.FC<TopBarProps> = ({ user }) => {
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const toast = useRef<Toast>(null);
  const bellRef = useRef<HTMLDivElement>(null);

  // Close the notifications dropdown when clicking anywhere else
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setNotificationsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Show the real name; fall back to the email's first part, then a plain label
  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'Super Admin';

  const hoverOn  = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.background = C.tint; e.currentTarget.style.borderColor = '#C6CDE8'; };
  const hoverOff = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = C.lineStrong; };

  return (
    <>
      <Toast ref={toast} />

      <div
        style={{
          height: '72px',
          background: 'white',
          borderBottom: '1px solid #E2E5F2',
          flexShrink: 0,
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 28px',
          gap: '22px',
          position: 'relative',
          zIndex: 100,
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        }}
      >
        {/* Left side - greeting */}
        <div style={{ color: C.text, lineHeight: 1.25, minWidth: 0 }}>
          <div style={{ fontSize: '0.816rem', fontWeight: 700, letterSpacing: '-0.2px' }}>Welcome back, {displayName.split(' ')[0]}</div>
          <div style={{ fontSize: '0.663rem', color: C.textSoft }}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>

        {/* Right side - Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          {/* Settings Button */}
          <button
            aria-label="Settings"
            onClick={() => setSettingsVisible(true)}
            style={iconBtn()}
            onMouseEnter={hoverOn}
            onMouseLeave={hoverOff}
          >
            <i className="pi pi-cog" style={{ fontSize: '0.918rem', color: C.navy }}></i>
          </button>

          {/* Notification Button */}
          <div ref={bellRef} style={{ position: 'relative' }}>
            <button
              aria-label="Notifications"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              style={iconBtn(notificationsOpen)}
              onMouseEnter={hoverOn}
              onMouseLeave={(e) => { if (!notificationsOpen) hoverOff(e); }}
            >
              <i className="pi pi-bell" style={{ fontSize: '0.918rem', color: C.navy }}></i>
              <span
                style={{
                  position: 'absolute',
                  top: '7px',
                  right: '8px',
                  width: '8px',
                  height: '8px',
                  background: C.gold,
                  borderRadius: '50%',
                  border: '2px solid white',
                }}
              ></span>
            </button>

            {/* Notifications Dropdown */}
            {notificationsOpen && (
              <div style={{
                position: 'absolute',
                top: '45px',
                right: '0',
                width: '288px',
                background: 'white',
                borderRadius: '14px',
                boxShadow: '0 12px 36px rgba(20, 38, 122, 0.16)',
                border: `1px solid ${C.line}`,
                overflow: 'hidden',
                animation: 'slideDown 0.2s ease'
              }}>
                <div style={{ padding: '13px 16px', borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '0.826rem', fontWeight: 700, color: C.text }}>Notifications</h3>
                  <span style={{ background: C.navy, color: C.gold, fontSize: '0.597rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>
                    3 New
                  </span>
                </div>
                <div style={{ maxHeight: '270px', overflowY: 'auto' }}>
                  {NOTIFICATIONS.map((notif, idx) => (
                    <div
                      key={idx}
                      style={{ padding: '11px 16px', borderBottom: idx < NOTIFICATIONS.length - 1 ? `1px solid ${C.tintSoft}` : 'none', cursor: 'pointer', transition: 'background 0.2s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.tintSoft; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', gap: '11px', alignItems: 'center' }}>
                        <div style={{ width: '31px', height: '31px', borderRadius: '9px', background: notif.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={`pi ${notif.icon}`} style={{ fontSize: '0.826rem', color: notif.fg }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 2px 0', fontSize: '0.734rem', color: C.text, fontWeight: 600 }}>{notif.text}</p>
                          <span style={{ fontSize: '0.643rem', color: C.textSoft }}>{notif.time}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '9px 16px', borderTop: `1px solid ${C.line}`, textAlign: 'center' }}>
                  <button style={{ background: 'transparent', border: 'none', color: C.navy, fontFamily: 'inherit', fontSize: '0.716rem', fontWeight: 700, cursor: 'pointer', padding: '4px' }}>
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default TopBar;